/**
 * Page Permission Hook
 *
 * Provides permission checking functionality for page-level access control.
 * Uses filterCardByPermissions (feature flags + session) on navigation page cards.
 */

import { NavigationPage } from '@/@cms/types/entities';
import { useFeatureFlags } from '@/@feature-flags/hooks/useFeatureFlags';
import { filterCardByPermissions } from '@/@studio/utils';
import { useSession } from '@/providers/enhanced-session';
import { useMemo } from 'react';

export interface PagePermissionResult {
    /** Whether the user has access to the page */
    hasAccess: boolean;
    /** Reason for access denial (if applicable) */
    reason?: string;
    /** Whether the page requires internal access */
    requiresInternal: boolean;
    /** Whether the page has client restrictions */
    hasClientRestrictions: boolean;
    /** Whether the page has user restrictions */
    hasUserRestrictions: boolean;
}

/**
 * Hook to check if a user has permission to access a specific page
 */
export function usePagePermissions(
    navigationPage: NavigationPage | null | undefined,
): PagePermissionResult {
    const { data: session } = useSession();
    const { hasFlag } = useFeatureFlags();

    return useMemo(() => {
        if (!navigationPage || !session?.user) {
            return {
                hasAccess: true,
                requiresInternal: false,
                hasClientRestrictions: false,
                hasUserRestrictions: false,
            };
        }

        const user = session.user;
        const userIsInternal = user.isInternal || false;

        // Internal users always have access to Strapi nav pages — individual
        // card visibility is controlled separately in the page render pass.
        if (userIsInternal) {
            return {
                hasAccess: true,
                requiresInternal: false,
                hasClientRestrictions: false,
                hasUserRestrictions: false,
            };
        }
        const userEmail = user.email || '';
        const userSalesforceOrg = session.org?.orgName;

        let requiresInternal = false;
        let hasClientRestrictions = false;
        let hasUserRestrictions = false;
        let hasAccessibleContent = false;
        let hasFeatureFlagRestrictions = false;

        for (const section of navigationPage.Sections || []) {
            for (const card of section.Cards || []) {
                const isInternal = card.Internal === true;
                const hasClients =
                    card.Clients && Array.isArray(card.Clients) && card.Clients.length > 0;
                const hasUsers = card.Users && Array.isArray(card.Users) && card.Users.length > 0;
                const isStudioAICard = card.Link === '/dani' || card.Link?.startsWith('/dani/');

                if (card.featureFlag) hasFeatureFlagRestrictions = true;
                if (isInternal) requiresInternal = true;
                if (hasClients) hasClientRestrictions = true;
                if (hasUsers && !isStudioAICard) hasUserRestrictions = true;

                const cardAccessible = filterCardByPermissions(
                    card,
                    userIsInternal,
                    userEmail,
                    userSalesforceOrg,
                    user.hasStudioAIAccess,
                    session.org?.hasClientStudioAIAccess,
                    { hasFlag },
                );

                if (cardAccessible) {
                    hasAccessibleContent = true;
                }
            }
        }

        if (
            !hasAccessibleContent &&
            (requiresInternal ||
                hasClientRestrictions ||
                hasUserRestrictions ||
                hasFeatureFlagRestrictions)
        ) {
            let reason = 'Access denied';
            if (hasFeatureFlagRestrictions) {
                reason = 'You do not have access to this feature';
            } else if (requiresInternal && !userIsInternal) {
                reason = 'This page requires internal access';
            } else if (hasClientRestrictions && !userSalesforceOrg) {
                reason = 'This page requires client access';
            } else if (hasUserRestrictions) {
                reason = 'This page has user-specific restrictions';
            }

            return {
                hasAccess: false,
                reason,
                requiresInternal,
                hasClientRestrictions,
                hasUserRestrictions,
            };
        }

        return {
            hasAccess: true,
            requiresInternal,
            hasClientRestrictions,
            hasUserRestrictions,
        };
    }, [navigationPage, session?.user, session?.org, hasFlag]);
}

/**
 * Hook to check if a user has permission to access a page by slug
 */
export function usePagePermissionsBySlug(slug: string | null | undefined): PagePermissionResult {
    const { data: session } = useSession();

    return useMemo(() => {
        if (!slug || !session?.user) {
            return {
                hasAccess: true,
                requiresInternal: false,
                hasClientRestrictions: false,
                hasUserRestrictions: false,
            };
        }

        return {
            hasAccess: true,
            requiresInternal: false,
            hasClientRestrictions: false,
            hasUserRestrictions: false,
        };
    }, [slug, session?.user]);
}
